import { Box, Button, Divider, Empty, Flex, Loading, Text } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useFundBusinesses } from '@/features/project/projects/detail/fund-bussiness/_hooks/fund-business';
import {
  FundBusinessFormTitleModifier,
  fundBusinessForm,
} from '@/features/project/projects/detail/fund-bussiness/_model/fund-business';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { AmountUnitLabelModifier } from '@/utils';
import { groupBy } from 'lodash-es';
import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import type { ProjectModel } from '../../../_models/project';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { CreateFundBusinessFormDrawer } from './CreateFundBusinessFormDrawer';
import { FundBusinessTable } from './FundBusinessTable';
import { UpdateFundBusinessFormDrawer } from './fund-business-update-form/UpdateFundBusinessFormDrawer';

type Project = z.infer<typeof ProjectModel>;

type Props = Pick<Project, 'fiscalYear'> & {
  editable: z.infer<typeof ProjectModel>['isLocked'];
};

export const FundBusinessInformation: FC<Props> = ({
  fiscalYear,
  editable,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const fundBusinesses = useFundBusinesses({ projectId });

  const groupByList = groupBy(fundBusinesses.data, 'businessForm');
  const fundBusinessList = Object.entries(groupByList).map(
    ([businessForm, data]) => {
      return {
        businessForm: fundBusinessForm.parse(businessForm),
        data,
      };
    },
  );

  if (fundBusinesses.isPending) {
    return <Loading />;
  }

  const isEmpty = !fundBusinesses.data || fundBusinesses.data.length === 0;

  return (
    <>
      {isEmpty ? (
        <Empty description={t('description-fund-business-not-found')} />
      ) : (
        fundBusinessList.map((fundBusiness, index) => {
          return (
            <Fragment key={fundBusiness.businessForm}>
              {index !== 0 && <Divider size="lg" />}
              <Flex align="center">
                <Text
                  variant="h5"
                  text={`${
                    FundBusinessFormTitleModifier[fundBusiness.businessForm]
                  }(${AmountUnitLabelModifier[unit]})`}
                />
                <Divider type="vertical" />
                <Text
                  variant="h5"
                  color="secondary"
                  text={t('count-and-amount')}
                />
              </Flex>
              <Box marginTop={16} marginBottom={12}>
                <Flex vertical gap={32}>
                  {fundBusiness.data.map((fundBusiness) => (
                    <PinpointMarker
                      key={fundBusiness.id}
                      pinpoint={getPinpointSceneAndFieldBySheetType({
                        pinpointSheetType: 'KS_STANDARD',
                        ksStandard: {
                          path: {
                            project: projectId,
                            fundBusinessId: fundBusiness.id,
                          },
                          field: 'ks.standard.fund-business-table',
                        },
                      })}
                    >
                      <FundBusinessTable
                        key={fundBusiness.id}
                        fundBusiness={fundBusiness}
                        fiscalYear={fiscalYear}
                      />
                    </PinpointMarker>
                  ))}
                </Flex>
              </Box>
              <ProjectResourceGuard onlyEdit>
                <PublishedProjectMutableGuard>
                  {editable && (
                    <Button
                      onClick={() => {
                        setDrawerState({
                          open: true,
                          isEditing: false,
                          type: 'fund-business',
                          fundSheetType: 'standard',
                          businessForm: fundBusiness.businessForm,
                        });
                      }}
                    >
                      {t('add-table')}
                    </Button>
                  )}
                </PublishedProjectMutableGuard>
              </ProjectResourceGuard>
            </Fragment>
          );
        })
      )}
      <CreateFundBusinessFormDrawer />
      <UpdateFundBusinessFormDrawer />
    </>
  );
};
